/* global google */
import { Box, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import Autocomplete from "react-google-autocomplete";
import { useTranslation } from "react-i18next";
import useGoogleMapsScript from "../../hooks/useGoogleMapsScript";
import "./styles.scss";

const AddressMapInput = ({
  placeholder = "Enter an address",
  onPlaceSelected,
  className = "",
  value,
  placeId,
  onChange,
  label,
  disabled = false,
  error = false,
  helperText = "",
  onBlur,
  mapIcon = false,
}) => {
  const autocompleteRef = useRef(null);
  const placesService = useRef(null);
  const isInitialPlaceLoad = useRef(true);
  const previousPlaceId = useRef(placeId);
  const [sessionToken, setSessionToken] = useState(null);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [inputValue, setInputValue] = useState(value || "");

  const { i18n, t } = useTranslation("districts");
  const googleMapsLanguage = i18n.language.split("-")[0] || "en";

  // Load Google Maps script with the mapped language
  const { isLoaded, error: scriptError } = useGoogleMapsScript(
    "AIzaSyACWu85k-bSAR3g7_5vdsmWBcQeJflrUzo", // API key
    googleMapsLanguage
  );

  function getAddressByPlaceId() {
    const shouldFetchPlace =
      placeId &&
      placeId !== "" &&
      placesService.current &&
      (isInitialPlaceLoad.current || placeId !== previousPlaceId.current);

    if (shouldFetchPlace) {
      isInitialPlaceLoad.current = false;
      previousPlaceId.current = placeId;
      if (!placesService.current) return;
      placesService.current.getDetails(
        {
          placeId: placeId,
          fields: [
            "address_components",
            "formatted_address",
            "geometry",
            "name",
          ],
          sessionToken: sessionToken || undefined,
          language: googleMapsLanguage,
        },
        (place, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK && place) {
            if (autocompleteRef.current?.refs?.input) {
              autocompleteRef.current.refs.input.value =
                place.formatted_address || "";
              setInputValue(place.formatted_address || "");
            }
            setSelectedPlace(place);
            handleOnPlaceSelected(place);
          }
        }
      );
    }
  }

  useEffect(() => {
    if (value) {
      setInputValue(value);
    }
  }, [value]);

  useEffect(() => {
    if (isLoaded && window.google && !placesService.current) {
      const attributionsDiv = document.createElement("div");
      attributionsDiv.style.display = "none";
      document.body.appendChild(attributionsDiv);
      placesService.current = new google.maps.places.PlacesService(
        attributionsDiv
      );
      return () => {
        if (document.body.contains(attributionsDiv)) {
          document.body.removeChild(attributionsDiv);
        }
      };
    }
  }, [isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      getAddressByPlaceId();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeId, isLoaded]);

  useEffect(() => {
    if (isLoaded) {
      setSessionToken(new google.maps.places.AutocompleteSessionToken());
    }
  }, [isLoaded]);

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      e.stopPropagation();
    }

    if (e.key === "Backspace" || e.key === "Delete") {
      if (inputValue === "") {
        setSelectedPlace(null);
        onPlaceSelected({
          address: "",
          place_id: "",
          city: "",
          neighborhood: "",
          street: "",
          building_number: "",
          country: "",
          district: "",
          latitude: "",
          longitude: "",
        });
      }
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    if (!newValue) {
      onPlaceSelected("");
    }
    if (newValue === "" && selectedPlace) {
      setSelectedPlace(null);
      if (onChange) onChange("");
    }
  };

  const handleOnPlaceSelected = (place) => {
    const getAddressComponent = (type) => {
      return (
        place.address_components?.find((component) =>
          component.types.includes(type)
        )?.long_name || ""
      );
    };

    // Enhanced district extraction with fallbacks for Israel
    const getDistrict = () => {
      // Try different administrative levels used in Israel
      let district = getAddressComponent("administrative_area_level_1");

      if (!district) {
        district = getAddressComponent("administrative_area_level_2");
      }

      if (!district) {
        district = getAddressComponent("administrative_area_level_3");
      }

      // For Israel, if no administrative area found, try to infer from city
      if (!district) {
        const city = getAddressComponent("locality");

        if (city) {
          // Map major Israeli cities to their districts (matching database values)
          const cityToDistrictMap = {
            // Jerusalem District (ID: 3)
            Jerusalem: t("Jerusalem District"),
            Yerushalayim: t("Jerusalem District"),
            Bethlehem: t("Jerusalem District"),
            "Beit Shemesh": t("Jerusalem District"),
            "Ma'ale Adumim": t("Jerusalem District"),

            // Tel Aviv District (ID: 4)
            "Tel Aviv": t("Tel Aviv District"),
            "Tel Aviv-Yafo": t("Tel Aviv District"),
            Holon: t("Tel Aviv District"),
            "Bnei Brak": t("Tel Aviv District"),
            "Ramat Gan": t("Tel Aviv District"),
            Givatayim: t("Tel Aviv District"),
            "Bat Yam": t("Tel Aviv District"),
            Herzliya: t("Tel Aviv District"),

            // Haifa District (ID: 2)
            Haifa: t("Haifa District"),
            Acre: t("Haifa District"),
            Akko: t("Haifa District"),
            Nahariya: t("Haifa District"),
            Karmiel: t("Haifa District"),

            // Center District (ID: 6)
            Netanya: t("Center District"),
            "Petah Tikva": t("Center District"),
            Rehovot: t("Center District"),
            "Rishon LeZion": t("Center District"),
            "Kfar Saba": t("Center District"),
            "Ra'anana": t("Center District"),
            "Hod HaSharon": t("Center District"),
            Lod: t("Center District"),
            Ramla: t("Center District"),

            // South District (ID: 5)
            "Beer Sheva": t("South District"),
            Beersheba: t("South District"),
            Ashdod: t("South District"),
            Ashkelon: t("South District"),
            Eilat: t("South District"),
            Dimona: t("South District"),
            Netivot: t("South District"),

            // North District (ID: 1)
            Nazareth: t("North District"),
            Safed: t("North District"),
            Tzfat: t("North District"),
            Tiberias: t("North District"),
            "Kiryat Shmona": t("North District"),
            Afula: t("North District"),
          };

          district = cityToDistrictMap[city] || "";
        }
      }

      return district;
    };

    setSelectedPlace(place);
    setInputValue(place.formatted_address || "");

    const updates = {
      address: place.formatted_address || "",
      place_id: place.place_id || "",
      city: getAddressComponent("locality"),
      neighborhood: getAddressComponent("sublocality_level_1"),
      street: getAddressComponent("route"),
      building_number: getAddressComponent("street_number"),
      country: getAddressComponent("country"),
      district: getDistrict(),
      latitude: place.geometry?.location?.lat() || "",
      longitude: place.geometry?.location?.lng() || "",
    };

    onPlaceSelected(updates);
    if (onChange) onChange(place.formatted_address || "");

    setSessionToken(new google.maps.places.AutocompleteSessionToken());
  };

  const handleBlur = (e) => {
    if (selectedPlace) {
      setInputValue(value);
    }
    if (selectedPlace && autocompleteRef.current?.refs?.input) {
      autocompleteRef.current.refs.input.value =
        selectedPlace.formatted_address || "";
      setInputValue(selectedPlace.formatted_address || "");
    } else if (!selectedPlace && inputValue) {
      setInputValue(value);
    }

    if (typeof onBlur === "function") {
      onBlur(e);
    }
  };

  if (scriptError) {
    return (
      <Box>
        <Typography color="error">Error loading Google Maps API</Typography>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box>
        <Typography>Loading Google Maps...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "8px",
        opacity: disabled ? 0.5 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {label ? (
        <Typography
          color="rgba(47, 43, 61, 0.9)"
          sx={{ fontSize: "15px", fontWeight: 500 }}
        >
          {label}
        </Typography>
      ) : null}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
        }}
      >
        <Box sx={{ position: "relative" }}>
          <Autocomplete
            key={googleMapsLanguage}
            ref={autocompleteRef}
            onPlaceSelected={handleOnPlaceSelected}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            value={inputValue}
            disabled={disabled}
            options={{
              types: ["geocode"],
              componentRestrictions: { country: "il" },
              language: googleMapsLanguage,
              sessionToken: sessionToken || undefined,
            }}
            placeholder={placeholder}
            className={`address-map-input ${
              error && helperText ? "error" : ""
            } ${className}`}
          />

          {mapIcon ? (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                right: "12px",
                display: "flex",
                transform: "translateY(-50%)",
              }}
            >
              <img src="/icons/google-maps-logo.svg" alt="google-maps-logo" />
            </Box>
          ) : null}
        </Box>
        {error && helperText && (
          <Box>
            <Typography
              sx={{
                color: "#d32f2f",
                fontSize: "13px",
              }}
            >
              {helperText}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default AddressMapInput;
